import { actorSchema, type UserCard } from "@-/dev.improve.haniverse.database.schemas.actor.schema";
import type { ActorDatabase, ActorTransaction } from "@-/dev.improve.haniverse.database.actor";
import type { ContentDatabase } from "@-/dev.improve.haniverse.database.content";
import {
  type GraphemeTaiwanUsageMap,
  getTaiwanTbclSet,
} from "@-/dev.improve.haniverse.data.taiwan-usage-map";
import { getComponentPrevalenceLookups } from "@-/dev.improve.haniverse.data.component-prevalence";
import { getGraphemePrevalence } from "@-/dev.improve.haniverse.lib.morpheme.grapheme.prevalence";
import type { HanziScript } from "@-/dev.improve.haniverse.lib.actor.types";
import {
  getSimplifiedLevels,
  getTraditionalLevels,
} from "@-/dev.improve.haniverse.assets.graphemes.levels";
import {
  type GraphemeLessonPart,
  getGraphemeLessonPart,
  isGraphemeReadingIntroducedAtGrade,
  orderIntroducedPinyinForGrapheme,
} from "@-/dev.improve.haniverse.lib.level";
import memoize from "@-/com.npmjs.memoize";
import { eq } from "@-/com.npmjs.drizzle-orm";
import {
  ensureUserCardRows,
  startUserCardSchedule,
} from "@-/dev.improve.haniverse.lib.cards.user-card.rows";

type CreateUserCardGrapheme = { front: string };

/**
 * The level that introduces each character in the script's curriculum: its curriculum grade (HSK or
 * TBCL) and its bundled introduction part. A character no level lists is absent.
 */
const getCurriculumPlacementByCharacter = memoize(
  (
    hanziScript: HanziScript,
  ): ReadonlyMap<string, { curriculumGrade: number; part: GraphemeLessonPart }> => {
    const placementByCharacter = new Map<
      string,
      { curriculumGrade: number; part: GraphemeLessonPart }
    >();
    const levels = hanziScript === "traditional" ? getTraditionalLevels() : getSimplifiedLevels();
    for (const level of levels) {
      for (const character of level.characters) {
        if (!placementByCharacter.has(character)) {
          placementByCharacter.set(character, {
            curriculumGrade: level.curriculumGrade ?? 1,
            part: getGraphemeLessonPart({
              grapheme: character,
              isPronunciationLesson: false,
              level,
            }),
          });
        }
      }
    }
    return placementByCharacter;
  },
);

/**
 * Whether automatic word-reading enrollment may introduce a grapheme's readings, from its
 * meaning card's label. A mnemonic-labelled component carries an invented memory cue (夬
 * "propose"). Lesson enrollment instead follows its bundled part, which can vary by lesson.
 */
export function isGraphemePronunciationEnrollable(meaningLabel: string | null): boolean {
  return meaningLabel !== "mnemonic";
}

/**
 * A card to enroll, by IDENTITY only: a meaning card is its grapheme, a pronunciation card its
 * `(grapheme, pinyin)` reading. Content ids and glosses are deliberately absent — both change
 * between content builds, so nothing stored in the actor DB may be keyed by or copied from them.
 */
export type GraphemeCardTarget =
  | { grapheme: string; kind: "meaning" }
  | { grapheme: string; kind: "pronunciation"; pinyin: string };

export type CreateUserCardArgs = {
  actorDb: ActorDatabase;
  contentDb: ContentDatabase;
  graphemeCard: CreateUserCardGrapheme;
  hanziScript: HanziScript;
  initialFsrsSchedule?: {
    due: string;
    scheduledDays: number;
  };
  startedStudyingAt?: string;
  twUsageMap: GraphemeTaiwanUsageMap;
};

/**
 * Which of a grapheme's cards to enroll. `lesson` (the default) follows the bundled introduction
 * part: primitive and glyph-only lessons enroll the meaning alone; combined lessons also enroll
 * their introduced readings. `all` includes the readings postponed by a glyph-only lesson when
 * seeding an "everything studied" deck. Primitives remain meaning-only in either scope.
 */
export type CreateUserCardScope = "all" | "lesson";

/**
 * Enroll each target in the deck inside `tx`: a missing card is created, a soft-deleted one comes
 * back as a new card, and an unscheduled one gets a schedule that starts over; a card that is
 * already scheduled is left alone. Returns the meaning card when `targetList` has one.
 *
 * The rows carry ids derived from the target (`ensureUserCardRows`), so two devices that enroll
 * the same card before they sync write the same rows. Everything enrollment decides is written by
 * the UPDATE at the end, never by the insert that creates the row.
 */
export async function enrollGraphemeCardTargets({
  dueIso,
  nowIso,
  scheduledDays,
  startedStudyingAt,
  targetList,
  tx,
}: {
  dueIso: string;
  nowIso: string;
  scheduledDays: number;
  startedStudyingAt: string;
  targetList: readonly GraphemeCardTarget[];
  tx: ActorTransaction;
}): Promise<UserCard | undefined> {
  let meaningCard: UserCard | undefined;

  for (const target of targetList) {
    // A pronunciation card's identity is `(grapheme, pinyin)`: its row ids are derived from the
    // content row's pinyin, never from anything content can reword.
    if (target.kind === "pronunciation" && target.pinyin === "") {
      throw new Error(`Pronunciation target for '${target.grapheme}' has no pinyin reading`);
    }
    const { ids } = await ensureUserCardRows(tx, target, nowIso);
    const existing = await tx.query.userCard.findFirst({ where: { id: ids.userCardId } });
    if (existing === undefined) {
      throw new Error(`Card rows for '${target.grapheme}' were not written`);
    }

    if (existing.fsrsCardId !== null) {
      if (target.kind === "meaning") {
        meaningCard = existing;
      }
      continue;
    }

    await startUserCardSchedule(tx, { dueIso, fsrsCardId: ids.fsrsCardId, nowIso, scheduledDays });
    const [enrolledCard] = await tx
      .update(actorSchema.userCard)
      .set({
        // Enrolling consumes any "skip until tomorrow" deferral: the card is in study now, so
        // nothing should hold it out of the flow later.
        deferredUntil: null,
        fsrsCardId: ids.fsrsCardId,
        // Enrolling also takes the card out of the learn queue.
        queuedAt: null,
        // A fresh enrollment is a fresh introduction; a stale balance from before a reset must
        // not pose as the latest review recharge.
        reviewRechargeAfter: null,
        startedStudyingAt,
      })
      .where(eq(actorSchema.userCard.id, ids.userCardId))
      .returning();

    if (target.kind === "meaning") {
      meaningCard = enrolledCard;
    }
  }

  return meaningCard;
}

/**
 * The cards of `grapheme` a lesson `part` enrolls: its meaning card and/or the pronunciation card of
 * every reading that lesson introduces ({@link isGraphemeReadingIntroducedAtGrade}). A meaning-only
 * lesson (`part: "meaning"`, Level 1 干) enrolls the meaning card alone — no pronunciation cards.
 * A pronunciation lesson (`part: "pronunciation"`, Level 2-P1) enrolls those readings as separate
 * pronunciation cards, including hardcoded opening ones (干 gan4). A meaning-and-pronunciation
 * lesson enrolls both. A higher reading is enrolled with the first word that uses it (see
 * {@link createUserWordReadingCards}).
 *
 * A primitive lesson enrolls its meaning cue alone even when the glyph has a dictionary reading.
 * Graphemes with no prevalent pinyin (strokes/radicals like 丨) have no pronunciation card to enroll.
 */
async function readGraphemeCardTargets({
  contentDb,
  grapheme,
  hanziScript,
  onlyPinyin,
  part,
  twUsageMap,
}: {
  contentDb: ContentDatabase;
  grapheme: string;
  hanziScript: HanziScript;
  /** Restrict enrollment to one reading (干 gan4, then 干 gan1 on separate 2-P1 lessons). */
  onlyPinyin?: string;
  part: GraphemeLessonPart;
  twUsageMap: GraphemeTaiwanUsageMap;
}): Promise<GraphemeCardTarget[]> {
  // The bundled lesson already specifies a primitive or glyph-only introduction. Every app
  // glyph gets a meaning row during content generation, so no content lookup is needed here.
  if (part === "primitive" || part === "meaning") {
    return [{ grapheme, kind: "meaning" }];
  }
  const pronunciationCardRows = await contentDb(
    import.meta.location,
  ).query.appGraphemePronunciationCard.findMany({
    columns: {
      grapheme: true,
      hsk2026Level: true,
      isMain: true,
      pinyin: true,
      tbclLevel: true,
    },
    where: { grapheme: { eq: grapheme } },
  });

  const curriculumGrade =
    getCurriculumPlacementByCharacter(hanziScript).get(grapheme)?.curriculumGrade ?? null;
  const introducedPronunciationCardByPinyin = new Map(
    pronunciationCardRows
      .filter((reading) =>
        isGraphemeReadingIntroducedAtGrade({
          curriculumGrade,
          grapheme,
          onlyPinyin,
          part,
          pinyin: reading.pinyin,
          reading,
          script: hanziScript,
        }),
      )
      .map((reading) => [reading.pinyin, reading]),
  );
  const introducedPronunciationCardRows = orderIntroducedPinyinForGrapheme(grapheme, [
    ...introducedPronunciationCardByPinyin.keys(),
  ]).map((pinyin) => {
    const reading = introducedPronunciationCardByPinyin.get(pinyin);
    if (reading === undefined) {
      throw new Error(`Missing introduced pronunciation card for ${grapheme} ${pinyin}`);
    }
    return reading;
  });

  // Graphemes with no prevalent pinyin (e.g. radicals/strokes like 丨) should not get a
  // pronunciation card variation. This mirrors the display-side rule in `usePrevalentPinyin`,
  // which only surfaces pronunciation for `common`/`rare` graphemes.
  const { simplified, traditional } = getComponentPrevalenceLookups();
  const hasPrevalentPinyin =
    getGraphemePrevalence(grapheme, hanziScript, {
      simplifiedComponentPrevalenceLookup: simplified,
      twComponentPrevalenceLookup: traditional,
      twTbclSet: getTaiwanTbclSet(),
      twUsageMap,
    }).asCharacter !== "obscure";

  const meaningTargets: GraphemeCardTarget[] =
    part === "pronunciation" ? [] : [{ grapheme, kind: "meaning" }];
  const pronunciationTargets: GraphemeCardTarget[] = hasPrevalentPinyin
    ? introducedPronunciationCardRows.map((row) => ({
        grapheme: row.grapheme,
        kind: "pronunciation",
        pinyin: row.pinyin,
      }))
    : [];
  return [...meaningTargets, ...pronunciationTargets];
}

/**
 * When a newly enrolled card is due: immediately (it is reviewed once its level is finished),
 * unless an admin/test seed pins a deterministic schedule.
 */
function introducedCardSchedule({
  initialFsrsSchedule,
  nowIso,
}: {
  initialFsrsSchedule: CreateUserCardArgs["initialFsrsSchedule"];
  nowIso: string;
}) {
  return {
    dueIso: initialFsrsSchedule?.due ?? nowIso,
    scheduledDays: initialFsrsSchedule?.scheduledDays ?? 0,
  };
}

/**
 * Server- and client-safe implementation of "introduce a new grapheme into the user's deck."
 * Inserts (or resurrects after a soft-delete) the grapheme's meaning card and, unless its level
 * introduces meanings only, the pronunciation card of every reading the character introduces
 * (see {@link readGraphemeCardTargets}), creating fresh FSRS rows for each. A meaning-only level's
 * character gets its pronunciation cards from its pronunciation-level lesson
 * ({@link createUserPronunciationCards}); `scope: "all"` enrolls them at once instead. Wraps the
 * per-grapheme writes in a single transaction so a card is never left half-created, and so sync
 * never uploads half of an enrollment.
 *
 * The pure form takes the resolved Taiwan usage map as an arg; React callers go through
 * {@link useCreateUserCard}, which fetches it from the QueryClient cache, and server callers
 * resolve it once per process. The component-prevalence closures and the TBCL glyph set are read
 * from precomputed build-time assets, so neither caller needs to supply a decomposition map.
 */
export async function createUserCard({
  actorDb,
  contentDb,
  graphemeCard,
  hanziScript,
  initialFsrsSchedule,
  part,
  scope = "lesson",
  startedStudyingAt = new Date().toISOString(),
  twUsageMap,
}: CreateUserCardArgs & {
  part?: Exclude<GraphemeLessonPart, "pronunciation">;
  scope?: CreateUserCardScope;
}): Promise<UserCard> {
  const nowIso = startedStudyingAt;
  const { dueIso, scheduledDays } = introducedCardSchedule({ initialFsrsSchedule, nowIso });
  const grapheme = graphemeCard.front;
  const curriculumPart = getCurriculumPlacementByCharacter(hanziScript).get(grapheme)?.part;
  const targetList = await readGraphemeCardTargets({
    contentDb,
    grapheme,
    hanziScript,
    part:
      part ??
      (curriculumPart === "primitive"
        ? "primitive"
        : scope === "all"
          ? "meaning-and-pronunciation"
          : (curriculumPart ?? "meaning-and-pronunciation")),
    twUsageMap,
  });

  const meaningCard = await actorDb(import.meta.location).transaction(async (tx) =>
    enrollGraphemeCardTargets({ dueIso, nowIso, scheduledDays, startedStudyingAt, targetList, tx }),
  );

  if (meaningCard === undefined || meaningCard.fsrsCardId === null) {
    throw new Error("Meaning card insertion succeeded but row was not returned");
  }

  return meaningCard;
}

/**
 * The pronunciation-level lesson's enrollment: the pronunciation card of every reading the
 * character introduces (see {@link readGraphemeCardTargets}), without touching its meaning card,
 * which its meaning-only level already enrolled. Idempotent like {@link createUserCard}.
 *
 * Pass `onlyPinyin` for a single-reading 2-P1 slot (干 gan4, then 干 gan1).
 */
export async function createUserPronunciationCards({
  actorDb,
  contentDb,
  graphemeCard,
  hanziScript,
  initialFsrsSchedule,
  onlyPinyin,
  startedStudyingAt = new Date().toISOString(),
  twUsageMap,
}: CreateUserCardArgs & { onlyPinyin?: string }): Promise<void> {
  const nowIso = startedStudyingAt;
  const { dueIso, scheduledDays } = introducedCardSchedule({ initialFsrsSchedule, nowIso });
  const targetList = await readGraphemeCardTargets({
    contentDb,
    grapheme: graphemeCard.front,
    hanziScript,
    onlyPinyin,
    part: "pronunciation",
    twUsageMap,
  });
  // A pronunciation lesson that enrolled nothing would stay the next lesson forever; fail visibly
  // instead (the pronunciation level only lists target characters with a reading).
  if (targetList.length === 0) {
    throw new Error(
      onlyPinyin === undefined
        ? `No pronunciation card to enroll for '${graphemeCard.front}'`
        : `No pronunciation card to enroll for '${graphemeCard.front}' (${onlyPinyin})`,
    );
  }
  await actorDb(import.meta.location).transaction(async (tx) =>
    enrollGraphemeCardTargets({ dueIso, nowIso, scheduledDays, startedStudyingAt, targetList, tx }),
  );
}
